// iter503 — HowToStartCard
// Reusable intro/how-it-works card pinned at the top of complex lab pages
// (Nutrition Lab, Mind & Body Lab). Collapsible — users dismiss it once they
// know the flow, then it stays hidden via localStorage.
//
// Pattern inspired by Levels Health "Get started" panel + Oura "Insights how-to".

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp, Sparkles, X, CheckCircle2 } from "lucide-react";

// iter505 — fire-and-forget telemetry to unified ab_events store.
// HowToStartCard owns its key (matches backend GUIDES registry).
function emitEvent(key, event_type, props) {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ab/event`;
    const payload = JSON.stringify({ key, variant: "default", event_type, props });
    // sendBeacon is reliable even during unmount/navigation
    const blob = new Blob([payload], { type: "application/json" });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      axios.post(url, JSON.parse(payload), { timeout: 3000 }).catch(() => {});
    }
  } catch { /* swallow telemetry errors */ }
}

/**
 * @param {object} props
 * @param {string} props.storageKey   — localStorage key (e.g. "nutramilo_howto_nutrition_lab")
 * @param {string} props.title        — short headline
 * @param {string} props.subtitle     — 1-sentence value prop
 * @param {Array<{title: string, body: string}>} props.steps — 3-5 ordered steps
 * @param {string[]} props.benefits   — 2-4 micro-benefit chips
 * @param {string} props.accent       — gradient class e.g. "from-violet-500 to-fuchsia-500"
 * @param {boolean} props.isDark      — theme flag
 */
export default function HowToStartCard({
  storageKey,
  title,
  subtitle,
  steps = [],
  benefits = [],
  accent = "from-violet-500 to-fuchsia-500",
  isDark = false,
  testid = "howto-start-card",
}) {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(`${storageKey}_collapsed`) !== "1";
    } catch { return true; }
  });
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`${storageKey}_dismissed`) === "1"; } catch { return false; }
  });

  // iter505 — fire impression once per session per guide key
  const impressionSent = useRef(false);
  useEffect(() => {
    if (dismissed || impressionSent.current) return;
    impressionSent.current = true;
    emitEvent(storageKey, "impression");
  }, [storageKey, dismissed]);

  if (dismissed) return null;

  const persist = (key, value) => {
    try { localStorage.setItem(key, value ? "1" : "0"); } catch {}
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    persist(`${storageKey}_collapsed`, !next);
  };

  const handleDismiss = () => {
    setDismissed(true);
    persist(`${storageKey}_dismissed`, true);
    emitEvent(storageKey, "dismiss");  // iter505
  };

  const handleStepClick = (idx) => {
    emitEvent(storageKey, "step_click", { step: idx + 1 });  // iter505
  };

  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl overflow-hidden border shadow-sm mb-4 ${
        isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200"
      }`}
    >
      {/* Gradient strip */}
      <div className={`h-1 bg-gradient-to-r ${accent}`} />

      {/* Header (always visible) */}
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center flex-shrink-0`}>
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <h3 className={`text-sm font-bold tracking-tight truncate ${isDark ? "text-white" : "text-slate-900"}`}>
              {title}
            </h3>
            <p className={`text-[11px] truncate ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {subtitle}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); handleDismiss(); } }}
            data-testid={`${testid}-dismiss`}
            className={`w-7 h-7 rounded-md flex items-center justify-center cursor-pointer ${
              isDark ? "hover:bg-slate-800 text-slate-400" : "hover:bg-slate-100 text-slate-500"
            }`}
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </span>
          {open ? <ChevronUp className="w-4 h-4 opacity-60" /> : <ChevronDown className="w-4 h-4 opacity-60" />}
        </div>
      </button>

      {/* Body (collapsible) */}
      {open && (
        <div className="px-4 pb-4 pt-1 space-y-3">
          {/* Benefit chips */}
          {benefits.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {benefits.map((b, i) => (
                <span
                  key={i}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] font-semibold ${
                    isDark ? "bg-slate-800 text-slate-300" : "bg-slate-100 text-slate-700"
                  }`}
                >
                  <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                  {b}
                </span>
              ))}
            </div>
          )}

          {/* Numbered steps */}
          <ol className="space-y-2">
            {steps.map((s, i) => (
              <li
                key={i}
                onClick={() => handleStepClick(i)}
                className={`flex items-start gap-3 p-2.5 rounded-lg cursor-pointer ${
                  isDark ? "bg-slate-950/50 border border-slate-800 hover:border-slate-700" : "bg-slate-50 border border-slate-100 hover:border-slate-200"
                }`}
                data-testid={`${testid}-step-${i + 1}`}
              >
                <div
                  className={`w-6 h-6 rounded-full bg-gradient-to-br ${accent} text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0`}
                >
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className={`text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                    {s.title}
                  </div>
                  <div className={`text-[11px] leading-relaxed mt-0.5 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                    {s.body}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
